import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import achieveImg from '../../assets/images/achieveImg.svg';
import sectionImg from '../../assets/images/sectionImg.svg';
import { formatDate } from '../custom-hooks';
import premiumIcon from '../../assets/icons/premium.svg';
import onPremiumIcon from '../../assets/icons/on.premium.svg';
import { Days } from '../../utils/constants';
import { useDispatch } from 'react-redux';
import { setRenewal } from '../../redux/reducers/userSlice';
import Moment from 'react-moment';
import moment from 'moment-timezone';
import 'moment-timezone';
import CustomModal from './CustomModal';
import AssignmentViewer from './AssignmentViewer';
import { prefetchAssignment } from '../../services/assignmentContent';
import CurriculumModal from './CurriculumModal';
import CustomInput from './CustomInput';
import UserService from '../../services/user.service';
import { toast } from 'react-toastify';
import { getCurriculumForPackage } from '../../utils/curriculumResolver';
import { buildPracticeLink } from '../../utils/practiceLink';
import type { CurriculumEntry } from '../../data/cohortCurriculum';
import {
    BookOpen,
    Calendar,
    Clock,
    GraduationCap,
    Layers,
    MonitorPlay,
    ScrollText,
    Sparkles,
    Upload,
    User,
    UserRound,
    Users,
    Video,
    ClipboardCheck,
    FileText,
} from 'lucide-react';

interface Props {
    setTab: (data: number) => void,
    item: any
}

function MetaChip({
    icon: Icon,
    label,
    value,
    allowWrap = false,
}: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
    allowWrap?: boolean;
}) {
    return (
        <div
            className={`flex min-w-0 items-center gap-2 rounded-xl border border-[#ebe4f0] bg-white px-2.5 py-2.5 ${
                allowWrap ? 'col-span-2 sm:col-span-3' : ''
            }`}
        >
            <Icon size={13} className="shrink-0 text-ryd-primary" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-[#94a3b8]">{label}</p>
                <p
                    className={`text-[12px] font-semibold capitalize text-[#334155] ${
                        allowWrap ? 'whitespace-normal break-words leading-snug' : 'truncate'
                    }`}
                >
                    {value}
                </p>
            </div>
        </div>
    );
}

export default function StudentCard({ setTab, item }: Props) {
    const dispatch = useDispatch();
    const [toggle, setToggle] = useState(false);
    const [program, setProgram] = useState(0);
    const [projectUrl, setProjectUrl] = useState("");
    const [hoverToggle, setHoverToggle] = useState(false);
    const [loading, setLoading] = useState(false);
    const [curriculumOpen, setCurriculumOpen] = useState(false);
    const [assignmentOpen, setAssignmentOpen] = useState(false);
    const [curriculumEntry, setCurriculumEntry] = useState<CurriculumEntry | null>(null);
    const userService = new UserService();

    const programRow = item?.programs?.[0];
    const cbtPracticeId = programRow?.package?.cbtPracticeId;
    const showTestButton = Boolean(cbtPracticeId);

    const pTime = moment();
    pTime.day(programRow?.day);
    pTime.hour(programRow?.time);
    pTime.second(0);
    pTime.minute(0);

    const cohort = programRow?.cohort;
    const hasAccess = cohort ? cohort.access !== false : false;
    const cohortFinished = cohort ? cohort.status === false : false;

    const initials = `${item?.firstName?.charAt(0) || ''}${item?.lastName?.charAt(0) || ''}`.toUpperCase();
    const programTitle = programRow?.package?.title || 'No program';
    const teacherName = [programRow?.teacher?.firstName, programRow?.teacher?.lastName]
        .filter(Boolean)
        .join(' ') || 'Not assigned';
    const cohortTitle = programRow?.cohort?.title || 'No cohort';
    const packageCurriculum = getCurriculumForPackage(programRow?.package);
    const assignmentUrl = String(programRow?.package?.assignmentFileUrl || '').trim();

    const handleCurriculumClick = () => {
        const entry = getCurriculumForPackage(programRow?.package);
        if (!entry) {
            toast.warn('Curriculum is not available for this program yet.');
            return;
        }
        setCurriculumEntry(entry);
        setCurriculumOpen(true);
    };

    const handleProjectUpdate = async () => {
        setLoading(true);
        try {
            const response = await userService.submitProjecttUrl({ projectUrl: projectUrl }, program);
            if (!response.status) {
                toast.error(response?.message);
                return;
            }
            toast.success('Project published successfully');
            setToggle(false);
            window.location.reload();
        } catch (err: any) {
            toast.error(err?.message);
        } finally {
            setLoading(false);
        }
    };

    const openLegacyPracticeLink = (data: any) => {
        const finalLink = buildPracticeLink(data);
        if (!finalLink) {
            toast.warn("Sorry this program doesn't have a practice link yet");
            return;
        }
        window.open(finalLink, '_blank', 'noopener,noreferrer');
    };

    const handleTestClick = async () => {
        if (!programRow) return;
        const popup = window.open('about:blank', '_blank');
        setLoading(true);
        try {
            const response = await userService.launchProgramPractice(programRow.id);
            if (response?.status && response?.data?.url) {
                if (popup) {
                    try {
                        popup.opener = null;
                    } catch (_) {
                        /* ignore */
                    }
                    popup.location.href = response.data.url;
                } else {
                    window.open(response.data.url, '_blank', 'noopener,noreferrer');
                }
                return;
            }
            popup?.close();
            const apiMsg = response?.message || 'Unable to open test';
            toast.error(/not found/i.test(apiMsg)
                ? 'Test launch is not available on this server yet. Use local API or deploy the latest backend.'
                : apiMsg);
        } catch (err: any) {
            popup?.close();
            const msg = err?.message || 'Unable to open test';
            toast.error(msg.includes('404') || msg.includes('not found')
                ? 'Test launch is not available on this server yet. Use local API or deploy the latest backend.'
                : msg);
        } finally {
            setLoading(false);
        }
    };

    const showRenewal =
        !programRow?.package?.isAdhoc &&
        ((hasAccess && (item.allowNewCohort === true || cohortFinished || (Array.isArray(item?.programs) && item.programs.some((p: any) => p.isPaid && p.isCompleted)))) || !hasAccess);

    const actionBtn =
        'inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2 text-[12px] font-semibold transition-all duration-200';

    return (
        <article className="group relative w-full overflow-hidden rounded-2xl border border-[#e8dce8] bg-white shadow-[0_8px_30px_rgba(136,22,122,0.08)] transition-shadow hover:shadow-[0_12px_40px_rgba(136,22,122,0.12)]">
            {/* Header banner */}
            <div className="relative overflow-hidden bg-gradient-to-br from-ryd-primary via-[#9c2788] to-[#6b115f] px-5 pb-14 pt-5">
                <img
                    src={sectionImg}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute -right-4 top-2 h-24 w-24 opacity-20"
                />
                <img
                    src={achieveImg}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-3 h-16 w-16 drop-shadow-lg"
                />
                <div className="relative z-10 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-white/30 bg-white/15 text-lg font-bold text-white backdrop-blur-sm">
                            {initials || '?'}
                        </div>
                        <div>
                            <h2 className="font-[AvertaStd-Semibold] text-xl capitalize leading-tight text-white lg:text-2xl">
                                {item?.firstName} {item?.lastName}
                            </h2>
                            <p className="mt-1.5 flex items-center gap-1.5 text-[17px] font-semibold leading-snug text-white sm:text-[18px]">
                                <GraduationCap size={18} className="shrink-0 opacity-90" />
                                {programTitle}
                            </p>
                        </div>
                    </div>
                    <span
                        className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                            hasAccess ? 'bg-emerald-400/90 text-emerald-950' : 'bg-rose-400/90 text-rose-950'
                        }`}
                    >
                        {hasAccess ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>

            {/* Floating program pill */}
            <div className="relative z-20 -mt-8 mx-5">
                <div className="flex items-center gap-2 rounded-xl border border-[#ebe4f0] bg-white px-4 py-3 shadow-md">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-ryd-primaryLess1 to-white">
                        <Sparkles size={18} className="text-ryd-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-[#8b9aab]">Current level</p>
                        <p className="truncate text-sm font-bold text-ryd-primary">
                            Level {Number(programRow?.package?.level) || '—'} · {programTitle}
                        </p>
                    </div>
                    {programRow?.package?.cbtPracticeName ? (
                        <span className="hidden shrink-0 rounded-lg bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-700 sm:inline">
                            Test: {programRow.package.cbtPracticeName}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Student & class info */}
            <div className="space-y-2 px-5 pb-5 pt-4">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <MetaChip icon={User} label="Gender" value={item?.gender || '—'} />
                    <MetaChip icon={Layers} label="Level" value={Number(programRow?.package?.level) || '—'} />
                    <MetaChip icon={Calendar} label="Day" value={Days[programRow?.day] || '—'} />
                    <MetaChip icon={Clock} label="Time" value={<Moment format="hh:mm A" date={pTime.toISOString()} />} />
                    <MetaChip icon={Users} label="Cohort" value={cohortTitle} />
                    <MetaChip
                        icon={Calendar}
                        label="Starts"
                        value={programRow?.cohort ? formatDate(programRow.cohort.startDate) : '—'}
                    />
                    <MetaChip icon={UserRound} label="Teacher" value={teacherName} allowWrap />
                </div>
            </div>

            {/* Actions */}
            <div className="border-t border-[#f0eaf0] bg-[#faf8fb] px-5 py-4">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Quick actions</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setTab(item?.id)}
                        className={`${actionBtn} border border-ryd-primary bg-ryd-primary text-white hover:bg-[#701466]`}
                    >
                        <MonitorPlay size={14} />
                        View activity
                    </button>
                    {hasAccess ? (
                        <Link
                            to={programRow?.teacher?.classLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${actionBtn} border border-ryd-primary text-ryd-primary hover:bg-ryd-primary hover:text-white`}
                        >
                            <Video size={14} />
                            Go to class
                        </Link>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => {
                            setProgram(programRow?.id);
                            setToggle(true);
                        }}
                        className={`${actionBtn} border border-[#cbd5e1] bg-white text-[#475569] hover:border-ryd-primary hover:text-ryd-primary`}
                    >
                        <Upload size={14} />
                        Publish project{programRow?.projectUrl ? ' ✓' : ''}
                    </button>
                </div>

                <p className="mb-3 mt-4 text-[11px] font-semibold uppercase tracking-wide text-[#8b9aab]">Learning tools</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleCurriculumClick}
                        disabled={!packageCurriculum}
                        className={`${actionBtn} border border-emerald-600 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        <ScrollText size={14} />
                        Curriculum
                    </button>
                    {assignmentUrl && programRow?.id ? (
                        <button
                            type="button"
                            onClick={() => setAssignmentOpen(true)}
                            onMouseEnter={() => prefetchAssignment(programRow.id)}
                            onFocus={() => prefetchAssignment(programRow.id)}
                            onTouchStart={() => prefetchAssignment(programRow.id)}
                            className={`${actionBtn} border border-amber-600 bg-amber-50 text-amber-800 hover:bg-amber-600 hover:text-white`}
                        >
                            <FileText size={14} />
                            Assignment
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => programRow && openLegacyPracticeLink(programRow)}
                        className={`${actionBtn} border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white`}
                    >
                        <BookOpen size={14} />
                        Practice
                    </button>
                    {showTestButton ? (
                        <button
                            type="button"
                            disabled={loading}
                            onClick={() => void handleTestClick()}
                            className={`${actionBtn} border border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white disabled:opacity-60`}
                        >
                            <ClipboardCheck size={14} />
                            {loading ? 'Opening…' : 'Test'}
                        </button>
                    ) : null}
                    {showRenewal ? (
                        <button
                            type="button"
                            onClick={() => dispatch(setRenewal((item as any)._fullChild ?? item))}
                            onMouseOver={() => setHoverToggle(true)}
                            onMouseOut={() => setHoverToggle(false)}
                            className={`${actionBtn} border border-red-700 bg-red-50 text-red-800 hover:bg-red-700 hover:text-white`}
                        >
                            <img src={hoverToggle ? premiumIcon : onPremiumIcon} alt="" className="h-3.5 w-3.5" />
                            Add new program
                        </button>
                    ) : null}
                </div>
            </div>

            {curriculumOpen && curriculumEntry ? (
                <CurriculumModal
                    entry={curriculumEntry}
                    packageTitle={programTitle}
                    onClose={() => {
                        setCurriculumOpen(false);
                        setCurriculumEntry(null);
                    }}
                />
            ) : null}

            {toggle && (
                <CustomModal
                    modalStyle="relative bg-white rounded-[16px] max-w-md"
                    closeModal={() => setToggle(false)}
                >
                    <div className="p-4 sm:p-8">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                                <Upload size={20} />
                            </div>
                            <div>
                                <h3 className="font-[AvertaStd-Semibold] text-lg text-ryd-subTextPrimary">Publish project</h3>
                                <p className="text-[13px] text-[#576877]">Share your child&apos;s project link with the class.</p>
                            </div>
                        </div>
                        <label className="mb-1 block text-[13px] text-ryd-subTextPrimary">Project URL</label>
                        <CustomInput
                            type="email"
                            placeholder="https://project-link.com"
                            required={true}
                            onChange={(e: any) => setProjectUrl(e.target.value)}
                        />
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={handleProjectUpdate}
                                className="w-full rounded-xl bg-ryd-primary px-5 py-3 text-[14px] font-semibold text-white"
                            >
                                {loading ? 'Updating…' : 'Update'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setToggle(false)}
                                className="w-full rounded-xl border border-gray-200 bg-gray-100 px-5 py-3 text-[14px] text-black"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </CustomModal>
            )}
            {assignmentOpen && programRow?.id ? (
                <AssignmentViewer
                    programId={programRow.id}
                    title={programTitle}
                    onClose={() => setAssignmentOpen(false)}
                />
            ) : null}
        </article>
    );
}
